import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import PasswordProtection from './components/PasswordProtection';
import CookieBanner from './components/CookieBanner';
import siteSettingsService from './services/siteSettingsService';
import { initEmailJS } from './services/emailService';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import DentistProfile from './components/DentistProfile';
import GetTreatedToday from './components/GetTreatedToday';
import Testimonials from './components/Testimonials';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import RegulatedBy from './components/RegulatedBy';
import Footer from './components/Footer';
import AdminDashboard from './pages/AdminDashboard';
import UserDashboard from './pages/UserDashboard';
import Login from './pages/Login';
import AvailabilityCheckPage from './pages/AvailabilityCheckPage';
import AppointmentConfirmationPage from './pages/AppointmentConfirmationPage';
import BookingTermsPage from './pages/BookingTermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RegisterPage from './pages/RegisterPage';

function App() {
  // State to control whether the site is password protected
  const [isProtected, setIsProtected] = useState(false);
  // State to store the password
  const [correctPassword, setCorrectPassword] = useState('dentalhero2025');
  // State to store whether the user has entered the correct password
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // State to track loading status
  const [loading, setLoading] = useState(true);

  // Load site settings from Firestore and initialize EmailJS on component mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await siteSettingsService.getSettings();
        setIsProtected(settings.isProtected);
        setCorrectPassword(settings.password);
        
        // Initialize EmailJS
        initEmailJS();
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading site settings:', error);
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Check if the user has previously authenticated in this session
  useEffect(() => {
    const authenticated = sessionStorage.getItem('authenticated');
    if (authenticated === 'true') {
      setIsAuthenticated(true);
    }
  }, []);
  
  // Custom setIsProtected function that updates Firestore
  const handleSetIsProtected = async (value) => {
    // Update local state immediately for responsive UI
    setIsProtected(value);
    
    // Update Firestore
    try {
      await siteSettingsService.updateProtectionStatus(value);
    } catch (error) {
      console.error('Error updating protection status:', error);
      // Revert local state if Firestore update fails
      setIsProtected(!value);
    }
  };

  // Function to handle successful password entry
  const handlePasswordSuccess = () => {
    setIsAuthenticated(true);
    // Store authentication in session storage
    sessionStorage.setItem('authenticated', 'true');
  };

  // Show loading indicator while fetching settings
  if (loading) {
    return (
      <>
        <CookieBanner />
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </>
    );
  }

  // If the site is protected and the user is not authenticated, show the password protection screen
  if (isProtected && !isAuthenticated) {
    return (
      <>
        <CookieBanner />
        <PasswordProtection correctPassword={correctPassword} onPasswordSuccess={handlePasswordSuccess} />
      </>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <CookieBanner />
      <Routes>
        <Route path="/admin" element={<AdminDashboard isProtected={isProtected} setIsProtected={handleSetIsProtected} />} />
        <Route path="/user" element={<UserDashboard />} />
        <Route path="/login" element={<Login />} />
        <Route path="/availability-check" element={<AvailabilityCheckPage />} />
        <Route path="/appointment-confirmation" element={<AppointmentConfirmationPage />} />
        <Route path="/booking-terms" element={<BookingTermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/" element={
          <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-grow">
              <Hero />
              <Services />
              <DentistProfile />
              <GetTreatedToday />
              <Testimonials />
              <Pricing />
              <Contact />
              <RegulatedBy />
            </main>
            <Footer />
          </div>
        } />
      </Routes>
    </Router>
  );
}

export default App;
